import { Turbo } from '@hotwired/turbo-rails'
import { Controller } from '@hotwired/stimulus'

import { Editor } from '../bpmn/editor'

import { patch, post } from '@rails/request.js'
import { useApplication } from 'stimulus-use'
import { beforePageUnload } from '../utils'

export default class extends Controller {
  static targets = [
    'canvas',
    'dropzone',
    'file',
    'diagramImportFile',
    'diagramImportLoading',
    'diagramImportJoke',
    'controls',
    'error',
    'fullscreenButton',
    'fullscreenContainer'
  ]

  static values = {
    url: String,
    autoLayoutUrl: String,
    cancelUrl: String,
    lintRules: Object,
    lintEnabled: {
      type: Boolean,
      default: true
    },
    diagramImportJokes: Array
  }

  connect () {
    useApplication(this)

    if (this.hasFullscreenButtonTarget && !document.fullscreenEnabled) {
      this.fullscreenButtonTarget.remove()
    }

    this.boundHandleFullscreenChange = this.handleFullscreenChange.bind(this)
    document.addEventListener('fullscreenchange', this.boundHandleFullscreenChange)
  }

  disconnect () {
    document.removeEventListener('fullscreenchange', this.boundHandleFullscreenChange)
    this.stopJokeRotation()

    if (this.bpmn) {
      this.bpmn.destroy()
      this.allowLeaving()
    }
  }

  beforeUnloadHandler = (event) => beforePageUnload(event)

  initializeCanvas () {
    if (this.isPreview) return
    if (!this.hasCanvasTarget) return

    if (!this.bpmn) {
      this.bpmn = new Editor({
        mode: 'modeler',
        container: this.canvasTarget,
        lintEnabled: this.lintEnabledValue,
        lintRules: this.lintRulesValue
      })

      this.bpmn.initialize()
    }

    this.bpmn.instance.on('import.done', (event) => {
      this.hideDropzone()

      if (event.warnings.length > 0) {
        for (const warning of event.warnings) {
          console.warn(warning)
        }
      }

      this.bpmn.redrawPalette()
    })

    this.bpmn.instance.on('element.changed', 1000, ({ element }) => {
      this.preventLeaving()

      if (element.type === 'bpmn:Participant') {
        this.bpmn.redrawPalette()
      }
    })

    if (this.hasFileTarget) {
      this.fileTarget.addEventListener('change', (event) => {
        const input = event.target
        const file = input.files[0]

        if (file) {
          this.bpmn.loadFromFile(file)
          input.value = ''
          this.showCanvas()
        }
      })
    }

    this.loadDiagram()
  }

  canvasTargetConnected () {
    this.initializeCanvas()
  }

  async loadEmptyDiagram () {
    await this.bpmn.loadEmptyDiagram()

    this.showCanvas()
  }

  showControls () {
    this.controlsTarget.classList.remove('group-[.inline-edit]:hidden')
  }

  hideControls () {
    this.controlsTarget.classList.add('group-[.inline-edit]:hidden')
  }

  showDropzone () {
    this.dropzoneTarget.classList.remove('hidden')
    this.canvasTarget.classList.add('hidden')
  }

  hideDropzone () {
    this.dropzoneTarget.classList.add('hidden')
  }

  showCanvas () {
    this.hideDropzone()
    this.showControls()
    this.canvasTarget.classList.remove('hidden')
  }

  hideCanvas () {
    this.canvasTarget.classList.add('hidden')
  }

  showError () {
    this.hideControls()
    this.canvasTarget.classList.add('blur-sm')
    this.errorTarget.classList.remove('hidden')
  }

  hideError () {
    this.canvasTarget.classList.remove('blur-sm')
    this.errorTarget.classList.add('hidden')
  }

  async loadDiagram () {
    this.showCanvas()

    const loaded = await this.bpmn.loadFromURL(this.urlValue)

    if (!loaded) {
      await this.showDropzone()
      this.hideControls()
    }
  }

  preventLeaving () {
    document.addEventListener('turbo:before-visit', this.beforeUnloadHandler)
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  allowLeaving () {
    document.removeEventListener('turbo:before-visit', this.beforeUnloadHandler)
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
  }

  async save () {
    this.allowLeaving()

    try {
      const bpmn = await this.bpmn.diagramXML()
      const body = { business_process: { bpmn } }
      const response = await patch(window.location.href,
        { body, responseKind: 'json' })

      if (response.ok) {
        Turbo.visit(response.response.url, { frame: this.turboFrameId })
      } else {
        this.showError()
        console.error('Response not successful', response)
      }
    } catch (err) {
      this.showError()
      console.error(err)
    }
  }

  async autoLayout (event) {
    const button = event.currentTarget
    const originalContent = button.innerHTML

    try {
      button.disabled = true
      button.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>'

      const bpmn = await this.bpmn.diagramXML()
      const response = await post(this.autoLayoutUrlValue, {
        body: { bpmn },
        responseKind: 'text'
      })

      if (response.ok) {
        const layoutedXml = await response.text
        await this.bpmn.loadFromXML(layoutedXml)
      } else {
        this.showError()
        console.error('Auto-layout failed', response)
      }
    } catch (err) {
      this.showError()
      console.error('Auto-layout error', err)
    } finally {
      button.disabled = false
      button.innerHTML = originalContent
    }
  }

  async download (event) {
    const fallback = this.turboFrameId.replace('turbo_frame', '')

    this.#downloadFile(`${event.target.dataset.filename || fallback}.bpmn`, await this.bpmn.diagramXML())
  }

  #downloadFile (filename, text) {
    const element = document.createElement('a')
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text))
    element.setAttribute('download', filename)
    element.style.display = 'none'

    document.body.appendChild(element)

    element.click()

    document.body.removeChild(element)
  }

  toggleFullscreen () {
    if (document.fullscreenElement === this.fullscreenContainerTarget) {
      document.exitFullscreen()
    } else {
      this.fullscreenContainerTarget.requestFullscreen()
    }
  }

  handleFullscreenChange () {
    if (!this.bpmn) return
    setTimeout(() => {
      this.bpmn.canvas.resized()
      this.bpmn.canvas.zoom('fit-viewport', 'auto')
      this.bpmn.redrawPalette()
    }, 200)
  }

  async submitDiagramImport () {
    if (!this.hasDiagramImportFileTarget || this.diagramImportFileTarget.files.length === 0) return

    const confirmed = await this.confirmDiagramImport()

    if (!confirmed) {
      this.diagramImportFileTarget.value = ''
      return
    }

    const file = this.diagramImportFileTarget.files[0]
    const url = this.diagramImportFileTarget.dataset.importUrl
    const body = new FormData()
    body.append('file', file)

    this.diagramImportFileTarget.value = ''

    const canvasWasVisible = !this.canvasTarget.classList.contains('hidden')
    this.showDiagramImportLoading()

    try {
      const response = await post(url, { body, responseKind: 'turbo-stream' })

      if (response.ok) {
        this.allowLeaving()
        this.vacateOccupancy()
        Turbo.visit(this.cancelUrlValue)
      } else {
        this.restoreAfterDiagramImportFailure(canvasWasVisible)
      }
    } catch (error) {
      this.restoreAfterDiagramImportFailure(canvasWasVisible)
      throw error
    } finally {
      this.hideDiagramImportLoading()
    }
  }

  restoreAfterDiagramImportFailure (canvasWasVisible) {
    if (canvasWasVisible) {
      this.showCanvas()
    } else {
      this.showDropzone()
    }
  }

  vacateOccupancy () {
    const occupancyElement = this.element.closest('[data-controller~="occupancy"]')
    if (!occupancyElement) return

    const occupancyController = window.Stimulus.getControllerForElementAndIdentifier(occupancyElement, 'occupancy')
    if (occupancyController) occupancyController.vacate()
  }

  confirmDiagramImport () {
    const dialog = document.getElementById('turbo-confirm')
    dialog.querySelector('h3').textContent = 'Replace existing diagram?'
    dialog.querySelector('p').textContent = 'This will replace the existing diagram and cannot be undone.'
    dialog.showModal()

    return new Promise((resolve) => {
      dialog.addEventListener('close', () => {
        resolve(dialog.returnValue === 'confirm')
      }, { once: true })
    })
  }

  showDiagramImportLoading () {
    this.hideDropzone()
    this.hideCanvas()

    if (this.hasDiagramImportJokeTarget && this.diagramImportJokesValue.length > 0) {
      this.diagramImportJokeTarget.textContent =
        this.diagramImportJokesValue[Math.floor(Math.random() * this.diagramImportJokesValue.length)]
      this.startJokeRotation()
    }

    if (this.hasDiagramImportLoadingTarget) {
      this.diagramImportLoadingTarget.classList.remove('hidden')
    }
  }

  hideDiagramImportLoading () {
    this.stopJokeRotation()

    if (this.hasDiagramImportLoadingTarget) {
      this.diagramImportLoadingTarget.classList.add('hidden')
    }
  }

  startJokeRotation () {
    this.jokeInterval = setInterval(() => {
      if (this.hasDiagramImportJokeTarget && this.diagramImportJokesValue.length > 0) {
        this.diagramImportJokeTarget.textContent =
          this.diagramImportJokesValue[Math.floor(Math.random() * this.diagramImportJokesValue.length)]
      }
    }, 5000)
  }

  stopJokeRotation () {
    if (this.jokeInterval) {
      clearInterval(this.jokeInterval)
      this.jokeInterval = null
    }
  }

  get turboFrameId () {
    return this.element.closest('turbo-frame')?.id
  }
}
